/**
 * Financial-year start month (1–12) for a country. AU and NZ run Jul–Jun; GB runs
 * Apr–Mar. Unrecognised or missing country falls back to the calendar year (Jan–Dec).
 */
export function getFinancialYearStartMonth(country) {
    if (country === 'AU' || country === 'NZ')
        return 7;
    if (country === 'GB')
        return 4;
    return 1;
}
function pad2(value) {
    return String(value).padStart(2, '0');
}
function lastDayOfMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** Financial year containing `date`, for an FY starting on `startMonth` (1–12). */
export function getFinancialYear(date, startMonth) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const fyStartYear = month >= startMonth ? year : year - 1;
    const isCalendarYear = startMonth === 1;
    const fyEndYear = isCalendarYear ? fyStartYear : fyStartYear + 1;
    const endMonth = isCalendarYear ? 12 : startMonth - 1;
    return {
        financialYear: isCalendarYear
            ? `${fyStartYear}`
            : `${fyStartYear}-${String(fyEndYear).slice(2)}`,
        startDate: `${fyStartYear}-${pad2(startMonth)}-01`,
        endDate: `${fyEndYear}-${pad2(endMonth)}-${pad2(lastDayOfMonth(fyEndYear, endMonth))}`,
    };
}
/**
 * Inverse of {@link getFinancialYear} — resolves an FY label ("2025-26", or "2026" for a
 * Jan-start FY) back to its date range, for endpoints that take the FY as input rather
 * than deriving it from "now" (e.g. the EOFY accountant pack's `financialYear` query
 * param).
 */
export function getFinancialYearRange(financialYear, startMonth) {
    const isCalendarYear = startMonth === 1;
    if (isCalendarYear) {
        const year = Number(financialYear);
        return { financialYear, startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }
    const fyStartYear = Number(financialYear.slice(0, 4));
    const fyEndYear = fyStartYear + 1;
    const endMonth = startMonth - 1;
    return {
        financialYear,
        startDate: `${fyStartYear}-${pad2(startMonth)}-01`,
        endDate: `${fyEndYear}-${pad2(endMonth)}-${pad2(lastDayOfMonth(fyEndYear, endMonth))}`,
    };
}
/**
 * Maps a date to its ATO BAS quarter. Always Jul–Jun regardless of the org's country —
 * BAS is an Australian statutory concept, not a general financial-year preset.
 */
export function getBasQuarter(date) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    let quarter;
    let fyStartYear;
    let startStr;
    let endStr;
    if (month >= 6 && month <= 8) {
        quarter = 1;
        fyStartYear = year;
        startStr = `${year}-07-01`;
        endStr = `${year}-09-30`;
    }
    else if (month >= 9 && month <= 11) {
        quarter = 2;
        fyStartYear = year;
        startStr = `${year}-10-01`;
        endStr = `${year}-12-31`;
    }
    else if (month <= 2) {
        quarter = 3;
        fyStartYear = year - 1;
        startStr = `${year}-01-01`;
        endStr = `${year}-03-31`;
    }
    else {
        quarter = 4;
        fyStartYear = year - 1;
        startStr = `${year}-04-01`;
        endStr = `${year}-06-30`;
    }
    const fyEndYear = fyStartYear + 1;
    const financialYear = `${fyStartYear}-${String(fyEndYear).slice(2)}`;
    return {
        label: `Q${quarter} FY${financialYear}`,
        quarter,
        financialYear,
        startDate: startStr,
        endDate: endStr,
    };
}
//# sourceMappingURL=financial-year.js.map